// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, useTheme } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { Breadcrumbs, Button, CircularProgress, Typography } from '@mui/material';
import { PROJECTS_ROUTE } from 'Utils/routes';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'Hooks';
import ProjectListItem from 'Features/projectList/ProjectListItem';
import stylesDef from './styles';
import FolderName from '../FolderName';
import { findFolderById, selectFolderList, selectProjectsInFolder } from '../folderListSelector';
import { getAllProjectsInFolderThunk, getAvailableProjectsThunk } from '../folderListThunk';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MoveProjectBulkDialog from './MoveProjectBulkDialog';
import { IAvailableProject, IAvailableProjectsResponse } from 'Utils/typings/state/folder';
import { SetProjectsInFolderAction } from '../folderListActions';

const FolderDetails = () => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const { folderId } = useParams();
  const dispatch = useAppDispatch();

  // Redux variables
  const folderList  = useAppSelector(
    selectFolderList
  );

  const projects = useAppSelector(
    selectProjectsInFolder
  );

  // Read from the store on every render so an inline rename shows immediately
  const folderDetails = findFolderById(folderList, folderId) ?? null;
  const [loading, setLoading] = useState<boolean>(false);
  const [showMoveProjectBulkDialog, setShowMoveProjectBulkDialog] = useState<boolean>(false);
  const [availableProjects, setAvailableProjects] = useState<IAvailableProject[]>([]);
  const [loadingAvailableProjects, setLoadingAvailableProjects] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    if (!folderId) return;

    // Find the folder details from the folders list
    const folder = findFolderById(folderList, folderId);

    // If the folder has projects, fetch them
    if (folder?.projectCount) {
      getProjectsInFolder(folderId);
    }
  }, [folderId]);

  // Methods
  const getProjectsInFolder = async (id: string) => {
    setLoading(true);

    // Fetch projects in the folder
    await dispatch(getAllProjectsInFolderThunk(id));

    setLoading(false);
  };

  const handleMoveProjectToFolderSuccess = (project?: IProject, folderId?: string) => {
    // Update the projects list with the new project
    const updatedProjects = projects.filter((p) => p._id !== project?._id);
    dispatch(SetProjectsInFolderAction(updatedProjects));
  }
  
  const handleMoveProjectBulkOpen = async () => {
    setLoadingAvailableProjects(true);
    setShowMoveProjectBulkDialog(true);
    const availableProjects: IAvailableProjectsResponse = await dispatch(getAvailableProjectsThunk(folderId as string));
    setAvailableProjects(availableProjects.projects);
    setLoadingAvailableProjects(false);
  };

  // HTML
  return (
    <Box sx={styles.mapviewLayoutLeft}>
      {/* Breadcrumb */}
      <Box sx={styles.breadcrumbsContainer}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Button component={RouterLink} to={PROJECTS_ROUTE} sx={styles.toolbarTextButton}>
            <Typography variant="body1" sx={styles.toolbarText}>All Projects</Typography>
          </Button>
          <FolderName initialFolderName={folderDetails?.name} folderId={folderDetails?._id} />
        </Breadcrumbs>
        {
          projects.length > 0 && (
            <Button variant="contained" color="primary" sx={styles.addProjectsButton} onClick={handleMoveProjectBulkOpen}>
              Add Projects
            </Button>
          )
        }
      </Box>
      <Box sx={{ ...(loading ? styles.projectsContainer : {}) }}>
        {/* Loading state */}
        {loading && (
          <Box sx={styles.circularProgressContainer}>
            <CircularProgress
              size={50}
            />
          </Box>
        )}

        {/* Render projects */}
        {!loading && projects.length > 0 && (
          <Box sx={styles.gridList}>
            {projects.map((project: any) => (
              <ProjectListItem
                project={project}
                key={project._id}
                isInsideFolder={true}
                handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
              />
            ))}
          </Box>
        )}

        {/* No projects found */}
        {!loading && !projects.length && (
          <Box sx={styles.noProjectsContainer}>
            <PlaceOutlinedIcon sx={styles.noProjectsIcon} />
            <Box sx={styles.noProjectsTextContainer}>
              <Typography variant="h3">
                There are no projects to show.
              </Typography>
              <Typography variant="body1">
                Click on the button below to start adding projects.
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={handleMoveProjectBulkOpen}>
              Add Projects
            </Button>
          </Box>
        )}
      </Box>
      <MoveProjectBulkDialog
        open={showMoveProjectBulkDialog}
        onClose={() => {
          setShowMoveProjectBulkDialog(false);
          getProjectsInFolder(folderId);
        }}
        availableProjects={availableProjects}
        loadingAvailableProjects={loadingAvailableProjects}
      />
    </Box>
  );
};

export default FolderDetails;
